import type {UseMutationOptions} from '@tanstack/react-query';
import {postInvenOpenPage} from '../api/viewer/postInvenOpenPage';
import {postViewerLastPage} from '../api/viewer/postViewerLastPage';
import {PagewebError} from '../errors/PagewebError.class';
import type {
  LastPageMutation,
  LastPageMutationVariables,
  OpenPageMutation,
  OpenPageMutationVariables,
} from '../graphql/generated/types';
import {ResultStatus} from '../graphql/generated/types';

/**
 * Viewer 도메인 Mutation Adapter
 */
export const viewerMutationAdapter = {
  /**
   * 마지막 페이지 저장
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 ResultStatus.Success enum 값 반환
   * - React Query error state 사용
   * - Factory 함수 없음
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(viewerMutationAdapter.lastPage());
   * mutation.mutate({input: {seriesId, productId, rate, spineIndex?, isDone?}});
   * // 성공: mutation.data === ResultStatus.Success
   * // 실패: mutation.isError
   * ```
   */
  lastPage: (): UseMutationOptions<
    LastPageMutation,
    PagewebError,
    LastPageMutationVariables
  > => ({
    mutationFn: async (variables: LastPageMutationVariables) => {
      await postViewerLastPage(variables.input);
      return {viewerLastPage: ResultStatus.Success};
    },
  }),
  /**
   * 페이지 오픈 로그 저장
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - ticketUid가 있으면 BigInt로 변환하여 전달
   * - 성공하면 ResultStatus.Success enum 값 반환
   * - try-catch 없음 (에러 발생 시 throw)
   */
  openPage: (): UseMutationOptions<
    OpenPageMutation,
    PagewebError,
    OpenPageMutationVariables
  > => ({
    mutationFn: async (variables: OpenPageMutationVariables) => {
      const {seriesId, productId, ticketUid} = variables.input;
      await postInvenOpenPage({
        seriesId,
        productId,
        ticketUid: ticketUid ? BigInt(ticketUid) : undefined,
      });
      return {viewerOpenPage: ResultStatus.Success};
    },
  }),
} as const;
